import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getFacilityMap,
  bulkCreateLocations,
  deactivateLocations,
  mergeLocations,
} from "@/lib/warehouse-map.functions";
import { useScope } from "@/hooks/use-scope";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/warehouse-map/locations")({
  component: LocationsTab,
});

type Row = {
  id: string; code: string; type: string;
  aisle: string | null; bay: string | null; shelf: string | null; bin: string | null;
  pickable: boolean; active: boolean;
  grid_x: number | null; grid_y: number | null;
};

function LocationsTab() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const getMap = useServerFn(getFacilityMap);
  const bulk = useServerFn(bulkCreateLocations);
  const deact = useServerFn(deactivateLocations);
  const merge = useServerFn(mergeLocations);

  const { data, isLoading } = useQuery({
    queryKey: ["wm-map", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getMap({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selectedIds = useMemo(
    () => Object.entries(selection).filter(([, v]) => v).map(([k]) => k),
    [selection],
  );

  const rows: Row[] = (data?.locations ?? []) as Row[];

  const columns: ColumnDef<Row, any>[] = [
    { accessorKey: "code", header: "Code", cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span> },
    { accessorKey: "type", header: "Type", cell: ({ row }) => <span className="capitalize">{row.original.type.replace("_", " ")}</span> },
    { id: "addr", header: "Aisle / Bay / Shelf / Bin", cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {[row.original.aisle, row.original.bay, row.original.shelf, row.original.bin].filter(Boolean).join(" / ") || "—"}
      </span>
    )},
    { id: "grid", header: "Grid", cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {row.original.grid_x != null ? `${row.original.grid_x},${row.original.grid_y}` : "—"}
      </span>
    )},
    { id: "flags", header: "Status", cell: ({ row }) => (
      <div className="flex gap-1">
        <Badge variant={row.original.pickable ? "default" : "secondary"}>{row.original.pickable ? "Pickable" : "Storage"}</Badge>
        {!row.original.active && <Badge variant="outline">Inactive</Badge>}
      </div>
    )},
  ];

  async function onDeactivate(active: boolean) {
    if (!tenantId || !selectedIds.length) return;
    await deact({ data: { tenantId, ids: selectedIds, active } });
    toast.success(`${selectedIds.length} location(s) ${active ? "activated" : "deactivated"}`);
    setSelection({});
    qc.invalidateQueries({ queryKey: ["wm-map", tenantId, facilityId] });
  }

  async function onMerge() {
    if (!tenantId || selectedIds.length < 2) return;
    const [keepId, ...mergeIds] = selectedIds;
    await merge({ data: { tenantId, keepId, mergeIds } });
    toast.success(`Merged ${mergeIds.length} into ${rows.find((r) => r.id === keepId)?.code ?? "keeper"}`);
    setSelection({});
    qc.invalidateQueries({ queryKey: ["wm-map", tenantId, facilityId] });
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {rows.length} location{rows.length === 1 ? "" : "s"}
          {selectedIds.length > 0 && ` · ${selectedIds.length} selected`}
        </div>
        <div className="flex gap-2">
          {selectedIds.length >= 2 && (
            <Button variant="outline" size="sm" onClick={onMerge}>
              Merge into first
            </Button>
          )}
          {selectedIds.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => onDeactivate(false)}>Deactivate</Button>
              <Button variant="outline" size="sm" onClick={() => onDeactivate(true)}>Activate</Button>
            </>
          )}
          <BulkCreateDialog
            onCreate={async (input) => {
              if (!tenantId || !facilityId) return;
              const r = await bulk({ data: { tenantId, facilityId, ...input } });
              toast.success(`Created ${r.count} location(s): ${r.first} … ${r.last}`);
              qc.invalidateQueries({ queryKey: ["wm-map", tenantId, facilityId] });
            }}
          />
        </div>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        getRowId={(r) => r.id}
        empty={{
          title: "No locations yet",
          description: "Use Bulk create to lay out an aisle of bays, shelves and bins.",
        }}
      />
    </div>
  );
}

function BulkCreateDialog({
  onCreate,
}: {
  onCreate: (input: {
    aisle: string; bays: number; shelves: number; bins: number;
    pickable: boolean; start_grid_x: number; start_grid_y: number;
    type: "storage" | "pick_face";
    location_size: "pallet" | "shelf" | "small_bin" | "medium_bin" | "large_bin" | "xlarge_bin" | null;
  }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [aisle, setAisle] = useState("A1");
  const [bays, setBays] = useState(10);
  const [shelves, setShelves] = useState(4);
  const [bins, setBins] = useState(2);
  const [startX, setStartX] = useState(2);
  const [startY, setStartY] = useState(2);
  const [pickable, setPickable] = useState(true);
  const [locationSize, setLocationSize] = useState<
    "pallet" | "shelf" | "small_bin" | "medium_bin" | "large_bin" | "xlarge_bin"
  >("shelf");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await onCreate({
        aisle, bays, shelves, bins,
        pickable, start_grid_x: startX, start_grid_y: startY,
        type: pickable ? "pick_face" : "storage",
        location_size: locationSize,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }
  const total = bays * shelves * bins;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Bulk create</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Bulk create locations</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Aisle code"><Input value={aisle} onChange={(e) => setAisle(e.target.value)} /></Field>
          <Field label="Bays"><Input type="number" value={bays} onChange={(e) => setBays(+e.target.value)} /></Field>
          <Field label="Shelves per bay"><Input type="number" value={shelves} onChange={(e) => setShelves(+e.target.value)} /></Field>
          <Field label="Bins per shelf"><Input type="number" value={bins} onChange={(e) => setBins(+e.target.value)} /></Field>
          <Field label="Grid start X"><Input type="number" value={startX} onChange={(e) => setStartX(+e.target.value)} /></Field>
          <Field label="Grid start Y"><Input type="number" value={startY} onChange={(e) => setStartY(+e.target.value)} /></Field>
          <div className="col-span-2 space-y-1">
            <Label className="text-xs text-muted-foreground">
              Container size <span className="opacity-70">(used for storage billing)</span>
            </Label>
            <Select value={locationSize} onValueChange={(v) => setLocationSize(v as typeof locationSize)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pallet">Pallet</SelectItem>
                <SelectItem value="shelf">Shelf</SelectItem>
                <SelectItem value="small_bin">Small bin</SelectItem>
                <SelectItem value="medium_bin">Medium bin</SelectItem>
                <SelectItem value="large_bin">Large bin</SelectItem>
                <SelectItem value="xlarge_bin">XLarge bin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={pickable} onChange={(e) => setPickable(e.target.checked)} />
            Pickable pick-face locations
          </label>
          <div className="col-span-2 text-xs text-muted-foreground">
            Will create <span className="font-medium text-foreground">{total}</span> locations, sequence {aisle}-B01-S01-N01 → {aisle}-B{String(bays).padStart(2,"0")}-S{String(shelves).padStart(2,"0")}-N{String(bins).padStart(2,"0")}.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || total < 1 || total > 5000}>Create {total}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}